/**
 * Firestore Connection Manager
 * Manages Firestore connection state and provides fallbacks
 */
import { getDocument, setDocument } from '../services/databaseService';
import { enableNetwork, disableNetwork, terminate } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

// Singleton state
let isConnected = true; // Assume connected initially
let connectionListeners = [];
let isRecovering = false; // Flag to prevent multiple recovery attempts at once
let isInOnboarding = false; // Track if we're in onboarding flow
let networkEnabled = true; // Track if network is enabled

// Connection check interval (in ms)
const CHECK_INTERVAL = 30000;
let checkIntervalId = null;

// Connection recovery attempts counter
let recoveryAttempts = 0;
const MAX_RECOVERY_ATTEMPTS = 3;

// Disable connection warnings by default
const DISABLE_CONNECTION_WARNINGS = true;

/**
 * Initialize the connection manager
 */
export const initConnectionManager = () => {
  // Reset recovery attempts counter on init
  recoveryAttempts = 0;

  // Start periodic checks
  if (!checkIntervalId) {
    checkIntervalId = setInterval(checkConnection, CHECK_INTERVAL);
    // Do an initial check, but don't wait for it
    setTimeout(() => {
      checkConnection().catch(err => {
        console.warn('Initial connection check failed, but continuing:', err);
      });
    }, 1000);
  }

  return () => {
    // Cleanup function
    if (checkIntervalId) {
      clearInterval(checkIntervalId);
      checkIntervalId = null;
    }
  };
};

/**
 * Set whether we're in onboarding flow
 * This will make the connection manager more permissive during onboarding
 */
export const setOnboardingMode = (inOnboarding) => {
  console.log(`Firestore connection manager: setting onboarding mode to ${inOnboarding ? 'enabled' : 'disabled'}`);

  // If we're already in the requested mode, do nothing
  if (isInOnboarding === inOnboarding) {
    console.log(`Onboarding mode already ${inOnboarding ? 'enabled' : 'disabled'}, no change needed`);
    return;
  }

  isInOnboarding = inOnboarding;

  // If we're entering onboarding mode, assume we're connected but DON'T try to enable network
  // This will prevent the "Unexpected state" errors during onboarding
  if (inOnboarding) {
    console.log('Entering onboarding mode, assuming connected state');
    isConnected = true;

    // Only notify listeners of the new connection state
    try {
      notifyListeners();
    } catch (error) {
      console.error('Error notifying listeners of onboarding mode change:', error);
    }
  }
};

/**
 * Check if we're in onboarding mode
 */
export const isOnboardingMode = () => {
  return isInOnboarding;
};

/**
 * Check Firestore connection status
 */
export const checkConnection = async () => {
  // Don't check if we're currently recovering
  if (isRecovering) {
    console.log('Skipping connection check - recovery in progress');
    return isConnected;
  }

  // If we're in onboarding mode, always return true and don't do any actual checks
  if (isInOnboarding) {
    if (!isConnected) {
      isConnected = true;
      notifyListeners();
    }
    return true;
  }

  try {
    // Only attempt to read if network is enabled
    if (networkEnabled) {
      // Try to read a simple document
      const testDocRef = doc(db, 'system', 'status');
      await getDoc(testDocRef);
    }

    // If we got here, connection is working or we're in a stable state
    if (!isConnected) {
      console.log('Firestore connection restored');
      isConnected = true;
      recoveryAttempts = 0; // Reset recovery attempts
      notifyListeners();

      // Only try to re-enable network if it was explicitly disabled and we're not in onboarding
      if (!networkEnabled && !isInOnboarding) {
        try {
          console.log('Re-enabling Firestore network after successful connection check');
          await enableNetwork(db);
          networkEnabled = true;
        } catch (enableError) {
          console.error('Error enabling Firestore network:', enableError);
          // Don't throw - just continue with the current state
        }
      }
    }

    return true;
  } catch (error) {
    // If we're in onboarding mode, don't treat this as a connection loss
    if (isInOnboarding) {
      console.log('Connection check failed in onboarding mode, but continuing anyway');
      return true;
    }

    // Special handling for "Unexpected state" errors - they require connection recovery
    if (error.message && error.message.includes('Unexpected state') && recoveryAttempts < MAX_RECOVERY_ATTEMPTS) {
      console.log('Detected "Unexpected state" error, attempting recovery');
      recoveryAttempts++;
      await recoverFirestoreConnection();
      return isConnected;
    }

    // If the error is a permission error, don't disable the network - might be normal during onboarding
    if (error.message && error.message.includes('Missing or insufficient permissions')) {
      console.log('Permission error detected, likely normal during onboarding');
      return true;
    }

    // For other errors, handle standard connection loss
    if (isConnected) {
      console.error('Firestore connection lost:', error);
      isConnected = false;
      notifyListeners();

      // Only disable network if it's not already disabled and we're not in onboarding
      if (networkEnabled && !isInOnboarding) {
        try {
          console.log('Disabling Firestore network to prevent errors');
          await disableNetwork(db);
          networkEnabled = false;
        } catch (disableError) {
          console.error('Error disabling Firestore network:', disableError);
          // Don't throw - just continue with the current state
        }
      }
    }

    return false;
  }
};

/**
 * Recover Firestore connection after internal state errors
 */
export const recoverFirestoreConnection = async () => {
  if (isRecovering) return false;

  isRecovering = true;
  console.log('Starting Firestore connection recovery process...');

  try {
    // Skip the usual recovery process for onboarding
    if (isInOnboarding) {
      console.log('In onboarding mode, using simplified recovery');
      isConnected = true;
      notifyListeners();
      isRecovering = false;
      return true;
    }

    // Normal recovery process
    try {
      // 1. Disable network first (ignore errors)
      console.log('Step 1: Disabling network...');
      await disableNetwork(db).catch(e => console.warn('Error during network disable (continuing):', e));
      networkEnabled = false;

      // 2. Wait a moment to let everything settle
      console.log('Step 2: Waiting for connections to settle...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      // 3. Re-enable network
      console.log('Step 3: Re-enabling network...');
      await enableNetwork(db).catch(e => console.warn('Error during network enable (continuing):', e));
      networkEnabled = true;

      console.log('Firestore connection recovery completed');
    } catch (err) {
      console.warn('Error during recovery process, but continuing:', err);
    }

    // Even if there were errors, assume we're connected now
    isConnected = true;
    notifyListeners();
    isRecovering = false;
    return true;
  } catch (error) {
    console.error('Error during Firestore connection recovery:', error);
    isRecovering = false;

    // For serious errors, just set connected to true and continue
    isConnected = true;

    return true;
  }
};

/**
 * Clear all Firestore connection state
 * This is a more aggressive way to recover from persistent connection issues
 */
export const clearConnectionState = async () => {
  if (isRecovering) {
    console.log('Already recovering, skipping clear connection state request');
    return true; // Return success to avoid blocking
  }

  isRecovering = true;
  console.log('Clearing Firestore connection state...');

  // For onboarding, use a simplified approach
  if (isInOnboarding) {
    console.log('In onboarding mode, using simplified connection state clearing');
    // Just set the state variables, don't try to interact with Firestore
    isConnected = true;
    networkEnabled = true;
    recoveryAttempts = 0;
    notifyListeners();
    isRecovering = false;
    return true;
  }

  try {
    // Try a much simpler approach first - just re-enable network
    try {
      console.log('Attempting to enable network only...');
      await enableNetwork(db);
      networkEnabled = true;
      console.log('Network enabled successfully, skipping full reset');

      isConnected = true;
      recoveryAttempts = 0;
      notifyListeners();
      isRecovering = false;
      return true;
    } catch (simpleError) {
      console.warn('Simple network enable failed, trying full reset:', simpleError);
    }

    // Full reset process - more likely to cause "Unexpected state" errors
    try {
      // 1. Disable network first (ignore errors)
      console.log('Step 1: Disabling network...');
      await disableNetwork(db).catch(e => console.warn('Error during network disable (continuing):', e));
      networkEnabled = false;

      // 2. Wait for connections to settle
      console.log('Step 2: Waiting for connections to settle...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      // 3. Re-enable network (this will create a fresh connection)
      console.log('Step 3: Re-enabling network...');
      await enableNetwork(db).catch(e => console.warn('Error during network enable (continuing):', e));
      networkEnabled = true;

      console.log('Firestore connection state cleared successfully');
    } catch (e) {
      console.warn('Error during connection state clearing, but continuing:', e);
    }

    // Even if there were errors, assume success
    isConnected = true;
    recoveryAttempts = 0;
    notifyListeners();
    isRecovering = false;
    return true;
  } catch (error) {
    console.error('Critical error clearing Firestore connection state:', error);

    // Even for critical errors, just set the connected state to true
    isConnected = true;
    recoveryAttempts = 0;
    isRecovering = false;
    return true;
  }
};

/**
 * Get current connection status
 */
export const getConnectionStatus = () => {
  if (DISABLE_CONNECTION_WARNINGS) {
    // Always return true to indicate connected
    return true;
  }

  return isConnected;
};

/**
 * Add a listener for connection status changes
 */
export const addConnectionListener = (callback) => {
  if (DISABLE_CONNECTION_WARNINGS) {
    // Return a no-op function
    return () => {};
  }

  connectionListeners.push(callback);
  return () => {
    connectionListeners = connectionListeners.filter(l => l !== callback);
  };
};

/**
 * Notify all listeners of connection status change
 */
const notifyListeners = () => {
  connectionListeners.forEach(listener => {
    try {
      listener(isConnected);
    } catch (error) {
      console.error('Error in connection listener:', error);
    }
  });
};

/**
 * Force enable network
 */
export const forceEnableNetwork = async () => {
  try {
    // Don't try to enable if we're in recovery mode
    if (isRecovering) {
      console.log('Skipping force enable - recovery in progress');
      return false;
    }

    // Force re-enable the network connection
    console.log('Attempting to manually enable network...');
    await enableNetwork(db);
    console.log('Network manually enabled');
    isConnected = true;
    notifyListeners();

    // Do a check after a short delay
    setTimeout(() => {
      checkConnection();
    }, 1000);

    return true;
  } catch (error) {
    console.error('Error manually enabling network:', error);

    // If we get an "Unexpected state" error, try recovery
    if (error.message && error.message.includes('Unexpected state')) {
      console.log('Attempting recovery after enable network error...');
      return await recoverFirestoreConnection();
    }

    return false;
  }
};

/**
 * Force disable network
 */
export const forceDisableNetwork = async () => {
  try {
    // Don't try to disable if we're in recovery mode
    if (isRecovering) {
      console.log('Skipping force disable - recovery in progress');
      return false;
    }

    // Force disable the network
    await disableNetwork(db);
    console.log('Network manually disabled');
    isConnected = false;
    notifyListeners();
    return true;
  } catch (error) {
    console.error('Error manually disabling network:', error);
    return false;
  }
};

/**
 * Reset connection state after authentication
 * Call this function after successful authentication to ensure Firestore is enabled
 */
export const resetConnectionState = async () => {
  console.log('Resetting Firestore connection state after authentication...');

  try {
    // Check if we have too many recovery attempts already
    if (recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
      console.log('Too many recovery attempts, trying more aggressive recovery...');
      return await clearConnectionState();
    }

    // Force enable network
    await enableNetwork(db);
    isConnected = true;
    notifyListeners();
    console.log('Firestore network re-enabled after authentication');

    // Trigger a connection check
    setTimeout(() => {
      checkConnection();
    }, 1000);

    return true;
  } catch (error) {
    console.error('Error resetting connection state:', error);

    // If we get an "Unexpected state" error, try recovery
    if (error.message && error.message.includes('Unexpected state')) {
      console.log('Attempting recovery after reset connection error...');
      return await recoverFirestoreConnection();
    }

    return false;
  }
};

/**
 * Simple check for Firestore connection that returns a boolean
 * @returns {Promise<boolean>} True if connected, false otherwise
 */
export const checkFirestoreConnection = async () => {
  try {
    // Use the system status document which is publicly readable per our security rules
    const testDocRef = doc(db, 'system', 'status');

    // Attempt to read the system status document
    // This will work if Firestore is connected and fail if not
    await getDoc(testDocRef);

    // If we get here, the connection is working
    return true;
  } catch (error) {
    console.warn('Firestore connection check failed:', error);

    // Check if error indicates network connectivity issues
    if (
      error.code === 'unavailable' ||
      error.code === 'failed-precondition' ||
      error.message.includes('offline')
    ) {
      return false;
    }

    // For permission errors, try a fallback approach
    if (error.message && error.message.includes('Missing or insufficient permissions')) {
      console.log('Permission error during connection check, using fallback approach');

      try {
        // Try to read a document in the connectionTests collection
        // This should work for authenticated users
        if (window.firebase && window.firebase.auth && window.firebase.auth().currentUser) {
          const userId = window.firebase.auth().currentUser.uid;
          const userTestDocRef = doc(db, 'connectionTests', userId);
          await getDoc(userTestDocRef);
          return true;
        }
      } catch (fallbackError) {
        console.warn('Fallback connection check also failed:', fallbackError);
      }
    }

    // For other types of errors, we still consider Firestore accessible
    // These might be permission errors, which means the network is fine
    return true;
  }
};
